import { Dropdown } from "primereact/dropdown";
import styles from "./KrogerReport.module.scss";
import { useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  itemSummaryColumns,
  itemSummaryExpansionColumns
} from "../../constants/table-columns/krogerReportTableColumns";
import * as constants from "../../constants/constants";
import { Calendar } from "primereact/calendar";
import { formatInTimeZone } from "date-fns-tz";
import Spinner from "../../components/spinner/spinner";
import { getItemSummary } from "../../services/KrogerReportService";
import { Toast } from "primereact/toast";

const ItemSummary = () => {
  const toast = useRef(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCreditReason, setSelectedCreditReason] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [itemNumber, setItemNumber] = useState("");
  const [expandedRows, setExpandedRows] = useState(null);
  const [itemSummaryData, setItemSummaryData] = useState([]);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);

  const krogerRegions = ["All", "TFTN", "TFNW", "TFSW", "TFTX"];
  const creditReasons = [
    "All",
    "Vendor Error",
    "Logistics Error",
    "Logistics Missed Delivery",
    "Logistic Delivery Error",
    "Logistic Handling"
  ];

  const convertToPacific = (date) => {
    if (!date) return null;
    return formatInTimeZone(
      new Date(date),
      "America/Los_Angeles",
      "yyyy-MM-dd'T'HH:mm:ss.SSS"
    );
  };

  const handleSearch = async () => {
    let isValid = true;
    if (!startDate) {
      setStartDateError("Start date is required");
      isValid = false;
    } else {
      setStartDateError("");
    }
    if (!endDate) {
      setEndDateError("End date is required");
      isValid = false;
    } else {
      setEndDateError("");
    }
    if (!isValid) {
      return;
    }
    try {
      setShowSpinner(true);
      const regions =
        selectedRegion === "All"
          ? krogerRegions.filter((region) => region !== "All")
          : selectedRegion
            ? [selectedRegion]
            : [];
      const creditReasonsPayload =
        selectedCreditReason === "All"
          ? creditReasons.filter((reason) => reason !== "All")
          : selectedCreditReason
            ? [selectedCreditReason]
            : [];
      const payload = {
        startDate: convertToPacific(startDate),
        endDate: convertToPacific(endDate),
        regions,
        creditReasons: creditReasonsPayload,
        invoiceNumber: invoiceNumber || "",
        itemNumber: itemNumber || ""
      };
      const response = await getItemSummary(payload);
      const rows = (response?.rows || []).map((row, index) => ({
        ...row,
        uniqueKey: `${row.invoiceNumber}-${row.itemNumber}-${index}`
      }));
      setItemSummaryData(rows);
      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      setItemSummaryData([]);
      toast?.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
    }
  };
  const handleExpandCollapseAll = () => {
    if (expandedRows && Object.keys(expandedRows).length > 0) {
      setExpandedRows(null);
    } else {
      const allExpandedRows = {};
      itemSummaryData?.forEach((row) => {
        allExpandedRows[row.uniqueKey] = true;
      });
      setExpandedRows(allExpandedRows);
    }
  };
  const rowExpansionTemplate = (data) => {
    return (
      <div className="p-1 pl-5 ml-5" style={{ background: "white" }}>
        <DataTable
          value={data?.items || []}
          tableStyle={{ minWidth: "50rem" }}
          scrollable
          emptyMessage="No items available"
        >
          {" "}
          {itemSummaryExpansionColumns?.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              sortable={col.sort}
              style={{ minWidth: col.minWidth }}
              headerStyle={{ minWidth: col.minWidth }}
            />
          ))}{" "}
        </DataTable>{" "}
      </div>
    );
  };
  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedRegion(null);
    setSelectedCreditReason(null);
    setInvoiceNumber("");
    setItemNumber("");
    setItemSummaryData([]);
    setExpandedRows(null);
  };

  return (
    <div>
      {showSpinner && <Spinner />}
      <Toast ref={toast} position="center" />
      <div className="grid">
        <div className="col-12 lg:col-2">
          <div className="mb-2">
            <label>Start Date</label>
            <span className="text-red-500">*</span>
          </div>
          <Calendar
            inputId="startDate"
            value={startDate}
            onChange={(e) => {
              setStartDateError("");
              setStartDate(e.value);
            }}
            maxDate={endDate ?? undefined}
            readOnlyInput
            showIcon
            placeholder="mm-dd-yy"
          />
          {startDateError && (
            <small className="p-error">{startDateError}</small>
          )}
        </div>
        <div className="col-12 lg:col-2">
          <div className="mb-2">
            <label>End Date</label>
            <span className="text-red-500">*</span>
          </div>
          <Calendar
            inputId="endDate"
            value={endDate}
            onChange={(e) => {
              setEndDateError("");
              setEndDate(e.value);
            }}
            minDate={startDate ?? undefined}
            readOnlyInput
            showIcon
            placeholder="mm-dd-yy"
          />
          {endDateError && <small className="p-error">{endDateError}</small>}
        </div>
        <div className="col-12 lg:col-2">
          <div className="mb-2">
            <label>Region</label>
          </div>
          <Dropdown
            options={krogerRegions}
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.value)}
            placeholder="Select"
            className={styles.itemDropdown}
          />
        </div>
        <div className="col-12 lg:col-2">
          <div className="mb-2">
            <label>Credit reason</label>
          </div>
          <Dropdown
            options={creditReasons}
            value={selectedCreditReason}
            onChange={(e) => setSelectedCreditReason(e.value)}
            className={styles.itemDropdown}
            placeholder="Select"
          />
        </div>
        <div className="col-12 lg:col-2">
          <div className="mb-2">
            <label>Invoice#</label>
          </div>
          <InputText
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            className={styles.itemInput}
          />
        </div>
        <div className="col-12 lg:col-2">
          <div className="mb-2">
            <label>Item#</label>
          </div>
          <InputText
            value={itemNumber}
            onChange={(e) => setItemNumber(e.target.value)}
            className={styles.itemInput}
          />
        </div>
        <div className="col-12 flex justify-content-end align-items-center gap-2">
          <div className="flex justify-content-center align-items-center h-full gap-2">
            <Button
              label="Search"
              type="submit"
              size="medium"
              className={styles?.primaryBtn}
              onClick={handleSearch}
            />
            <Button
              label="Reset"
              type="button"
              size="medium"
              className={styles?.resetBtn}
              onClick={handleReset}
            />
            <Button className={styles.downloadButton}>
              <i className="pi pi-download" />
              <span>Download</span>
            </Button>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <DataTable
          className={styles.regionDataGrid}
          value={itemSummaryData}
          tableStyle={{ minWidth: "50rem" }}
          scrollable
          rows={10}
          paginator={constants?.paginator}
          rowsPerPageOptions={constants?.rowsPerPageOptions}
          paginatorTemplate={constants?.paginatorTemplate}
          currentPageReportTemplate={constants?.currentPageReportTemplate}
          emptyMessage={constants?.emptyMessage}
          dataKey="uniqueKey"
          scrollHeight="30vw"
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
        >
          <Column
            expander
            style={{ width: "3rem" }}
            header={
              <Button
                icon={
                  expandedRows &&
                  Object.keys(expandedRows)?.length === itemSummaryData.length
                    ? "pi pi-chevron-down"
                    : "pi pi-chevron-right"
                }
                text
                rounded
                size="small"
                onClick={handleExpandCollapseAll}
              />
            }
          />
          {itemSummaryColumns?.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              sortable={col.sort}
              style={{ minWidth: col.minWidth }}
            />
          ))}
        </DataTable>
      </div>
    </div>
  );
};

export default ItemSummary;
